/*
 * Reading a CSAT summary.
 *
 * Kept out of the screen because both of these are judgement calls that would otherwise be
 * invisible inside a render tree, and both are easy to get quietly wrong.
 */

/**
 * The bars of the score histogram.
 *
 * Drawn from the survey's own scale rather than from the scores that happen to be present,
 * so a score nobody ever gives still gets its (empty) bar — "nobody ever rates us 1" is a
 * finding, and a histogram built from the keys in the data hides it.
 *
 * Each bar is measured against the tallest bar rather than against the total: with forty
 * answers on one score and two on another, bars drawn as a share of the total are all
 * invisible.
 */
export const scaleBars = (survey, summary) => {
  if (!survey || !summary) {
    return [];
  }
  const scoreQuestion = (survey.questions || []).find(
    question => question.id === survey.scoreQuestionId
  );
  const scale = scoreQuestion && scoreQuestion.scale;
  if (!scale) {
    return [];
  }

  const counts = summary.histogram || {};
  const highest = Object.keys(counts).reduce(
    (found, key) => Math.max(found, Number(counts[key]) || 0),
    0
  );

  const bars = [];
  for (let score = scale.min; score <= scale.max; score += 1) {
    const count = Number(counts[String(score)]) || 0;
    bars.push({
      score,
      count,
      percent: highest > 0 ? Math.round((count / highest) * 100) : 0
    });
  }
  return bars;
};

/**
 * Bars for a SCALE question's own histogram (e.g. the CES "ease" question), drawn over the
 * full min..max so an empty score still shows, and measured against the tallest bar — the
 * same reading as the headline score's histogram, reused for the secondary scales.
 */
export const histogramBars = (histogram, min, max) => {
  if (!histogram || min == null || max == null) {
    return [];
  }
  const highest = Object.keys(histogram).reduce(
    (found, key) => Math.max(found, Number(histogram[key]) || 0),
    0
  );
  const bars = [];
  for (let score = min; score <= max; score += 1) {
    const count = Number(histogram[String(score)]) || 0;
    bars.push({ score, count, percent: highest > 0 ? Math.round((count / highest) * 100) : 0 });
  }
  return bars;
};

/**
 * Bars for a choice question's distribution (the "what to improve" driver): one row per
 * option, in the survey's own order, measured against the most-picked option.
 */
export const choiceBars = choices => {
  const list = choices || [];
  const highest = list.reduce((found, choice) => Math.max(found, Number(choice.count) || 0), 0);
  return list.map(choice => {
    const count = Number(choice.count) || 0;
    return {
      value: choice.value,
      label: choice.labelEn || choice.labelTh || choice.value,
      count,
      percent: highest > 0 ? Math.round((count / highest) * 100) : 0
    };
  });
};

export default scaleBars;
